import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { PermissionWrapper } from "../../helpers/permission.js";

const InvestorCharterManagement = () => {
  const [investorCharters, setInvestorCharters] = useState([]);
  const [addPage, setAddPage] = useState("/Settings/InvestorCharter/add");
  const [deleteId, setDeleteId] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");

  // 🔎 Search Filter
  const [descriptionFilter, setDescriptionFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    // Simulating fetching data from an API or static data
    const fetchedInvestorCharters = [
      { id: 1, description: "Data for the month ending" },
      { id: 2, description: "Trend of monthly disposal of complaints" },
      { id: 3, description: "Trend of annual disposal of complaints" },
      { id: 4, description: "Investor rights and responsibilities" },
      { id: 5, description: "Grievance redressal mechanism" },
      { id: 6, description: "Investment products overview" },
      { id: 7, description: "Risk factors disclosure" },
      { id: 8, description: "Charges and fees structure" },
      { id: 9, description: "Performance benchmarks" },
      { id: 10, description: "Regulatory compliance statement" },
      { id: 11, description: "Annual financial report" },
      { id: 12, description: "Quarterly performance update" },
    ];
    setInvestorCharters(fetchedInvestorCharters);

    // Simulate role-based URL assignment for the add button
    setAddPage("/Settings/InvestorCharter/add");
  }, []);

  // 🔎 Apply filters
  const filteredCharters = useMemo(() => {
    return investorCharters.filter((charter) => {
      if (
        descriptionFilter &&
        !(charter.description || "")
          .toLowerCase()
          .includes(descriptionFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [investorCharters, descriptionFilter]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredCharters.length);
    setPageCount(Math.ceil(filteredCharters.length / limit));
    setPage(1);
  }, [filteredCharters, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedCharters = filteredCharters.slice(start - 1, end);

  const handleEdit = (id) => {
    window.location.href = `/Settings/InvestorCharter/edit/${id}`;
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowModal(true);
    setModalContent(
      <div>
        <h5>Are you sure you want to delete this record?</h5>
        <button onClick={confirmDelete} className="btn btn-danger">
          Yes, delete it!
        </button>
        <button onClick={closeModal} className="btn btn-secondary">
          No, cancel!
        </button>
      </div>
    );
  };

  const confirmDelete = () => {
    setInvestorCharters(
      investorCharters.filter((item) => item.id !== deleteId)
    );
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent("");
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Add any alert message here */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Investor Charter</h4>
          </div>
          <div className="col-md-6 text-right">
            <PermissionWrapper module="settings_investorcharter" action="add">
              <Link to={addPage} className="btn btn-default btn-sm">
                <FontAwesomeIcon icon={faPlus} /> Add Investor Charter
              </Link>
            </PermissionWrapper>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* 📑 Pagination Controls */}
          {investorCharters.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div className="panel-heading border-0">
                <h4 className="panel-title m-0">Investor Charters</h4>
              </div>
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table m-b-0">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>Description</th>
                  <th style={{ width: "50px" }}>Action</th>
                </tr>
                {/* 🔎 Search Filter Row */}
                <tr>
                  <th></th>
                  <th>
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "180px" }}
                    >
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search description..."
                        style={{
                          width: "100%",
                          fontSize: "12px",
                          padding: "3px 6px",
                          height: "28px",
                        }}
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => setDescriptionFilter("")}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCharters.length > 0 ? (
                  paginatedCharters.map((charter, index) => (
                    <tr key={charter.id}>
                      <td
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{charter.description}</td>
                      <td style={{ width: "80px" }}>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="settings_investorcharter"
                            action="edit"
                          >
                            <Link
                              to={`/Settings/InvestorCharter/edit/${charter.id}`}
                              className="btn btn-warning btn-xs"
                              title="Edit Investor Charter"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </PermissionWrapper>
                          <PermissionWrapper
                            module="settings_investorcharter"
                            action="delete"
                          >
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDelete(charter.id)}
                              title="Delete Investor Charter"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: "center" }}>
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div id="myModal" className="modal">
            <div className="modal-dialog panel panel-default panel-border top">
              <div className="modal-content">{modalContent}</div>
            </div>
          </div>
        )}
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default InvestorCharterManagement;
